import React, { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextVatReturn } from './VatReturn';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { VatReturnDA } from '../../../../services/admin/vat.return.service';
import { StatementDA } from '../../../../services/admin/statement.service';
import { VatReturn, VatCase } from '../../../../models/vat.return.model';
import { Period } from '../../../../models/period.model';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { taxMethodCompanyValue } from '../../../../libraries/tax.method.company.library';
import { taxDeclarationCompanyValue } from '../../../../libraries/tax.declaration.company.library';
import { modalShow } from '../../../../utils/modal.util';

import AppPreloader from '../../../../components/Preloader';
import AppModalVatAdjustmentEdit from '../../../../components/modal/ModalVatAdjustmentEdit';

export interface VatReturnListPageProps {};

let errorResponse: Error;

const AGGREGATE_CODES = new Set(['200', '289', '299', '379', '399', '479', '500', '510']);

const OPERATORS: Record<string, string> = {
  '221': '+', '225': '+', '230': '+', '235': '+', '280': '+',
  '289': '\u2212', '299': '=',
  '303': '+', '313': '+', '323': '+', '333': '+', '343': '+', '383': '+', '379': '=', '399': '=',
  '405': '+', '415': '\u2212', '420': '\u2212', '479': '=',
  '500': '=', '510': '=',
};

const INPUT_TAX_RIGHT = new Set(['479', '500', '510']);
const SECTION_I_RIGHT = new Set(['200', '289', '299']);

// Candidate base/tax pairs — only rendered if both cases exist in payload
const RATE_PAIRS: Array<{ baseCode: string; taxCode: string }> = [
  { baseCode: '302', taxCode: '303' },
  { baseCode: '312', taxCode: '313' },
  { baseCode: '322', taxCode: '323' },
  { baseCode: '332', taxCode: '333' },
  { baseCode: '342', taxCode: '343' },
];

// Candidate codes for each section — rows only rendered if present in payload
const SECTION_I_COUNTER = ['200', '205'];
const SECTION_I_DEDUCTIONS = ['220', '221', '225', '230', '235', '280'];
const SECTION_I_SUBTOTALS = ['289', '299'];
const ACQUISITIONS_PAIR = { baseCode: '382', taxCode: '383' };
const INPUT_TAX_CODES = ['400', '405', '410', '415', '420'];
const BALANCE_CODES = ['500', '510'];
const SECTION_III_CODES = ['900', '910'];

function formatRate(rate?: number | null): string {
  if (rate == null) return ''
  return (rate * 100).toFixed(2) + '%'
}

const VatReturnListPage: React.FunctionComponent<VatReturnListPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany} = UseOutletContextVatReturn()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const { id_period } = useParams<{ id_period: string }>()

  const [mounted, setMounted] = useState(false)
  const [periods, setPeriods] = useState<Period[] | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>(null)
  const [vatReturn, setVatReturn] = useState<VatReturn | null>(null)
  const [loading, setLoading] = useState(false)
  const [editingCase, setEditingCase] = useState<{code: string, label: string, amount: number, note: string, vouchers: any[]}>({code: '', label: '', amount: 0, note: '', vouchers: []})
  const [transitioning, setTransitioning] = useState(false)

  const isFiled = selectedPeriod?.status === 'filed'

  const reloadAfterTransition = async () => {
    if (!companyForUser) return
    await loadPeriods(companyForUser.id)
  }

  const handleClosePeriod = async () => {
    if (!companyForUser || !selectedPeriod) return
    const result = await GlobalSwal.fire({
      title: lang.labels.closePeriod,
      text: lang.labels.confirmClosePeriod,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.closePeriod,
      cancelButtonText: lang.labels.cancel,
    })
    if (!result.isConfirmed) return

    setTransitioning(true)
    try {
      const response = await StatementDA.closePeriod(companyForUser.id, selectedPeriod.id)
      if (response.status === 200) {
        const updatedPeriod = { ...selectedPeriod, status: 'closed' }
        setSelectedPeriod(updatedPeriod as Period)
        loadVatReturn(companyForUser.id, selectedPeriod.id)
        reloadAfterTransition()
      } else {
        const error = response.data as Error
        GlobalSwal.fire({ title: getError(error.code, lang.code, error.message), icon: 'error' })
      }
    } catch {
      GlobalSwal.fire({ title: lang.labels.error, icon: 'error' })
    } finally {
      setTransitioning(false)
    }
  }

  const handleFilePeriod = async () => {
    if (!companyForUser || !selectedPeriod) return
    const result = await GlobalSwal.fire({
      title: lang.labels.filePeriod,
      html: `<p>${lang.labels.confirmFilePeriod}</p><p class="fw-bold text-danger">${lang.labels.confirmFilePeriodWarning}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.filePeriod,
      cancelButtonText: lang.labels.cancel,
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return

    setTransitioning(true)
    try {
      const response = await StatementDA.filePeriod(companyForUser.id, selectedPeriod.id)
      if (response.status === 200) {
        const updatedPeriod = { ...selectedPeriod, status: 'filed' }
        setSelectedPeriod(updatedPeriod as Period)
        loadVatReturn(companyForUser.id, selectedPeriod.id)
        reloadAfterTransition()
      } else {
        const error = response.data as Error
        GlobalSwal.fire({ title: getError(error.code, lang.code, error.message), icon: 'error' })
      }
    } catch {
      GlobalSwal.fire({ title: lang.labels.error, icon: 'error' })
    } finally {
      setTransitioning(false)
    }
  }

  const handleReopenPeriod = async () => {
    if (!companyForUser || !selectedPeriod) return
    const result = await GlobalSwal.fire({
      title: lang.labels.reopenPeriod,
      text: lang.labels.confirmReopenPeriod,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.reopenPeriod,
      cancelButtonText: lang.labels.cancel,
    })
    if (!result.isConfirmed) return

    setTransitioning(true)
    try {
      const response = await StatementDA.reopenPeriod(companyForUser.id, selectedPeriod.id)
      if (response.status === 200) {
        const updatedPeriod = { ...selectedPeriod, status: 'open' }
        setSelectedPeriod(updatedPeriod as Period)
        loadVatReturn(companyForUser.id, selectedPeriod.id)
        reloadAfterTransition()
      } else {
        const error = response.data as Error
        GlobalSwal.fire({ title: getError(error.code, lang.code, error.message), icon: 'error' })
      }
    } catch {
      GlobalSwal.fire({ title: lang.labels.error, icon: 'error' })
    } finally {
      setTransitioning(false)
    }
  }

  const handleEditManualCase = (vatCase: VatCase) => {
    setEditingCase({code: vatCase.code, label: vatCase.label, amount: vatCase.amount ?? 0, note: vatCase.note ?? '', vouchers: vatCase.vouchers ?? []})
    modalShow('modal-vat-adjustment-edit')
  }

  const handleAdjustmentSaved = () => {
    if (companyForUser && selectedPeriod) {
      loadVatReturn(companyForUser.id, selectedPeriod.id)
    }
  }

  const casesMap = (): Map<string, VatCase> => {
    const map = new Map<string, VatCase>()
    if (vatReturn) {
      vatReturn.cases.forEach(c => map.set(c.code, c))
    }
    return map
  }

  const loadPeriods = async (id_company: string) => {
    await StatementDA.getPeriods(id_company, '', '').then((response) => {
      if (response.status === 200) {
        const raw = response.data
        let tempPeriods: Period[]

        if (Array.isArray(raw)) {
          tempPeriods = raw.map((item: any) => ({
            ...item,
            id: item.id ?? item._id ?? '',
            vat_net: item.vat_net ?? 0,
            output: item.output ?? 0,
            input: item.input ?? 0
          }))
        } else {
          tempPeriods = raw?.periods ?? []
        }

        setPeriods(tempPeriods)

        const match = id_period ? tempPeriods.find(p => p.id === id_period || (p as any)._id === id_period) : null
        const openPeriod = tempPeriods.find(p => p.status === 'open')
        const defaultPeriod = match ?? openPeriod ?? (tempPeriods.length > 0 ? tempPeriods[0] : null)

        if (defaultPeriod) {
          setSelectedPeriod(defaultPeriod)
        }
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
        setPeriods([])
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
      setPeriods([])
    })
  }

  const loadVatReturn = async (id_company: string, id_period: string) => {
    setLoading(true)
    await VatReturnDA.getVatReturn(id_company, id_period).then((response) => {
      if (response.status === 200) {
        setVatReturn(response.data)
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
        setVatReturn(null)
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
      setVatReturn(null)
    }).finally(() => {
      setLoading(false)
    })
  }

  const handleSelectPeriod = (period: Period) => {
    setSelectedPeriod(period)
    navigate(`/main/accounting/vat-return/view/${period.id}`, { replace: true })
  }

  const handleClickCase = (vatCase: VatCase) => {
    if (vatCase.type !== 'manual' && selectedPeriod) {
      navigate(`/main/accounting/vat-return/view/${selectedPeriod.id}/case/${vatCase.code}`)
    }
  }

  useEffect(() => {
    if (companyForUser) {
      loadPeriods(companyForUser.id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect(() => {
    if (companyForUser && selectedPeriod) {
      loadVatReturn(companyForUser.id, selectedPeriod.id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, selectedPeriod])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.accounting, branch: lang.labels.vatReturn}, company: true})
    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  const editButton = (vatCase: VatCase) => (
    <button className="btn btn-icon btn-sm btn-light-primary ms-2 py-0 px-1" type="button" onClick={(e) => { e.stopPropagation(); handleEditManualCase(vatCase) }} title={lang.labels.edit}>
      <span className="svg-icon svg-icon-6">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path opacity="0.3" d="M21.4 8.35303L19.241 10.511L13.485 4.755L15.643 2.59595C16.0248 2.21423 16.5426 2 17.0825 2C17.6224 2 18.1402 2.21423 18.522 2.59595L21.4 5.47394C21.7817 5.85575 21.9959 6.37353 21.9959 6.91349C21.9959 7.45344 21.7817 7.97122 21.4 8.35303Z" fill="black"/>
          <path d="M3.68699 21.932L9.88699 19.865L4.13099 14.109L2.06399 20.309C1.98815 20.5354 1.97703 20.7787 2.03189 21.0111C2.08674 21.2436 2.2054 21.4561 2.37449 21.6248C2.54359 21.7934 2.75641 21.9115 2.989 21.9658C3.22158 22.0201 3.46497 22.0084 3.68699 21.932Z" fill="black"/>
        </svg>
      </span>
    </button>
  )

  // Generic row renderer for Section I (4-col: code, label, left-amount, right-amount)
  const renderSectionIRow = (code: string, map: Map<string, VatCase>) => {
    const vatCase = map.get(code)
    if (!vatCase) return null
    const isAggregate = AGGREGATE_CODES.has(code)
    const operator = OPERATORS[code] ?? ''
    const isClickable = vatCase.type !== 'manual'
    const isManual = vatCase.type === 'manual'
    const isRight = SECTION_I_RIGHT.has(code)
    const number = vatCase.amount !== null ? formatMoney(vatCase.amount) : '\u2014'
    const amountText = operator ? `${operator}\u2002${number}` : number

    return (
      <tr
        key={code}
        className={`${isAggregate ? 'bg-light' : ''} ${isClickable ? 'cursor-pointer' : ''}`}
        style={isAggregate ? { borderTop: '1px solid #dee2e6' } : undefined}
        onClick={() => isClickable && handleClickCase(vatCase)}
      >
        <td className="text-muted fw-semibold fs-8 py-2" style={{ width: 55 }}>{code}</td>
        <td className={`py-2 ${isAggregate ? 'fw-bold text-dark' : isClickable ? 'text-dark text-hover-primary' : 'text-gray-700'}`}>
          {vatCase.label}
          {isManual && <span className="badge badge-light-warning ms-2 fs-9">{lang.labels.manual}</span>}
          {isManual && editButton(vatCase)}
        </td>
        <td className={`text-end py-2 ${isAggregate ? 'fw-bold text-dark' : 'fw-semibold'}`} style={{ minWidth: 120 }}>
          {!isRight ? amountText : ''}
        </td>
        <td className={`text-end py-2 ${isAggregate ? 'fw-bold text-dark' : 'fw-semibold'}`} style={{ minWidth: 120 }}>
          {isRight ? amountText : ''}
        </td>
      </tr>
    )
  }

  // Generic row for input-tax / balance section (4-col: code, label, left-amount, right-amount)
  const renderInputTaxRow = (code: string, map: Map<string, VatCase>) => {
    const vatCase = map.get(code)
    if (!vatCase) return null
    const isAggregate = AGGREGATE_CODES.has(code)
    const operator = OPERATORS[code] ?? ''
    const isClickable = vatCase.type !== 'manual'
    const isManual = vatCase.type === 'manual'
    const isRight = INPUT_TAX_RIGHT.has(code)
    const number = vatCase.amount !== null ? formatMoney(vatCase.amount) : '\u2014'
    const amountText = operator ? `${operator}\u2002${number}` : number

    return (
      <tr
        key={code}
        className={`${isAggregate ? 'bg-light' : ''} ${isClickable ? 'cursor-pointer' : ''}`}
        style={isAggregate ? { borderTop: '1px solid #dee2e6' } : undefined}
        onClick={() => isClickable && handleClickCase(vatCase)}
      >
        <td className="text-muted fw-semibold fs-8 py-2" style={{ width: 55 }}>{code}</td>
        <td className={`py-2 ${isAggregate ? 'fw-bold text-dark' : isClickable ? 'text-dark text-hover-primary' : 'text-gray-700'}`}>
          {vatCase.label}
          {isManual && <span className="badge badge-light-warning ms-2 fs-9">{lang.labels.manual}</span>}
          {isManual && editButton(vatCase)}
        </td>
        <td className={`text-end py-2 ${isAggregate ? 'fw-bold text-dark' : 'fw-semibold'}`} style={{ minWidth: 120 }}>
          {!isRight ? amountText : ''}
        </td>
        <td className={`text-end py-2 ${isAggregate ? 'fw-bold text-dark' : 'fw-semibold'}`} style={{ minWidth: 120 }}>
          {isRight ? amountText : ''}
        </td>
      </tr>
    )
  }

  // Generic row for Section III (4-col: code, operator, label, amount)
  const renderRow = (code: string, map: Map<string, VatCase>) => {
    const vatCase = map.get(code)
    if (!vatCase) return null
    const isAggregate = AGGREGATE_CODES.has(code)
    const operator = OPERATORS[code] ?? ''
    const isClickable = vatCase.type !== 'manual'
    const isManual = vatCase.type === 'manual'

    return (
      <tr
        key={code}
        className={`${isAggregate ? 'bg-light' : ''} ${isClickable ? 'cursor-pointer' : ''}`}
        style={isAggregate ? { borderTop: '1px solid #dee2e6' } : undefined}
        onClick={() => isClickable && handleClickCase(vatCase)}
      >
        <td className="text-muted fw-semibold fs-8 py-2" style={{ width: 55 }}>{code}</td>
        <td className="text-center text-muted fs-8 py-2" style={{ width: 30 }}>{operator}</td>
        <td className={`py-2 ${isAggregate ? 'fw-bold text-dark' : isClickable ? 'text-dark text-hover-primary' : 'text-gray-700'}`}>
          {vatCase.label}
          {isManual && <span className="badge badge-light-warning ms-2 fs-9">{lang.labels.manual}</span>}
          {isManual && editButton(vatCase)}
        </td>
        <td className={`text-end py-2 ${isAggregate ? 'fw-bold text-dark' : 'fw-semibold'}`} style={{ minWidth: 120 }}>
          {vatCase.amount !== null ? formatMoney(vatCase.amount) : '\u2014'}
        </td>
      </tr>
    )
  }

  // Rate pair row (6-col: baseCode, label, base-amount, taxCode, rate%, tax-amount)
  const renderRateLine = (pair: { baseCode: string; taxCode: string }, map: Map<string, VatCase>) => {
    const baseCase = map.get(pair.baseCode)
    const taxCase = map.get(pair.taxCode)
    if (!baseCase && !taxCase) return null

    const isBaseClickable = baseCase ? baseCase.type !== 'manual' : false
    const isTaxClickable = taxCase ? taxCase.type !== 'manual' : false
    const clickable = isBaseClickable || isTaxClickable

    const handleRowClick = () => {
      if (isTaxClickable && taxCase) handleClickCase(taxCase)
      else if (isBaseClickable && baseCase) handleClickCase(baseCase)
    }

    const baseNumber = baseCase && baseCase.base !== null ? formatMoney(baseCase.base) : baseCase && baseCase.amount !== null ? formatMoney(baseCase.amount) : '\u2014'
    const taxNumber = taxCase && taxCase.amount !== null ? formatMoney(taxCase.amount) : '\u2014'
    const taxOperator = OPERATORS[pair.taxCode] ?? ''
    const taxText = taxOperator ? `${taxOperator}\u2002${taxNumber}` : taxNumber
    const rateDisplay = formatRate(taxCase?.rate ?? baseCase?.rate)

    return (
      <tr
        key={pair.baseCode}
        className={clickable ? 'cursor-pointer' : ''}
        onClick={handleRowClick}
      >
        <td className="text-muted fw-semibold fs-8 py-2" style={{ width: 55 }}>{pair.baseCode}</td>
        <td className={`py-2 ${clickable ? 'text-dark text-hover-primary' : 'text-gray-700'}`}>
          {baseCase?.label ?? taxCase?.label ?? ''}
        </td>
        <td className={`text-end py-2 fw-semibold ${clickable ? 'text-dark text-hover-primary' : 'text-gray-700'}`} style={{ minWidth: 120 }}>
          {baseNumber}
        </td>
        <td className="text-muted fw-semibold fs-8 py-2" style={{ width: 55 }}>{pair.taxCode}</td>
        <td className="text-end py-2 text-muted fw-semibold fs-8" style={{ minWidth: 70 }}>
          {rateDisplay}
        </td>
        <td className={`text-end py-2 fw-semibold ${clickable ? 'text-dark text-hover-primary' : 'text-gray-700'}`} style={{ minWidth: 120 }}>
          {taxText}
        </td>
      </tr>
    )
  }

  // Aggregate summary row (6-col, spanning)
  const renderAggregateTaxRow = (code: string, map: Map<string, VatCase>, position: 'base' | 'tax') => {
    const vatCase = map.get(code)
    if (!vatCase) return null
    const num = vatCase.amount !== null ? formatMoney(vatCase.amount) : '\u2014'

    return (
      <tr className="bg-light" style={{ borderTop: '1px solid #dee2e6' }}>
        <td className="text-muted fw-semibold fs-8 py-2" style={{ width: 55 }}>{code}</td>
        <td className="py-2 fw-bold text-dark">{vatCase.label}</td>
        { position === 'base'
          ? <>
              <td className="text-end py-2 fw-bold text-dark" style={{ minWidth: 120 }}>{`=\u2002${num}`}</td>
              <td colSpan={3}></td>
            </>
          : <>
              <td></td>
              <td></td>
              <td></td>
              <td className="text-end py-2 fw-bold text-dark" style={{ minWidth: 120 }}>{`=\u2002${num}`}</td>
            </>
        }
      </tr>
    )
  }

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && periods
        ?
        <>
          {/* Period selector card */}
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h3>{lang.labels.vatReturn}</h3>
              </div>
              <div className="card-toolbar">
                { periods.length > 0 &&
                  <Dropdown>
                    <Dropdown.Toggle variant="btn btn-sm btn-light-primary fw-bold">
                      { selectedPeriod
                        ? `${selectedPeriod.name} (${dateFormat(lang.code, selectedPeriod.start_date)} – ${dateFormat(lang.code, selectedPeriod.end_date)})`
                        : lang.labels.selectOption
                      }
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-auto p-3 fs-7" align="end">
                      { periods.map((p, i) => (
                        <Dropdown.Item key={i} bsPrefix="menu-item" onClick={() => handleSelectPeriod(p)}>
                          <p className={`menu-link fw-bold px-3 py-1 m-0 ${selectedPeriod?.id === p.id ? 'text-primary' : ''}`}>
                            {p.name} ({dateFormat(lang.code, p.start_date)} – {dateFormat(lang.code, p.end_date)})
                          </p>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                }
              </div>
            </div>
            { selectedPeriod && vatReturn &&
              <div className="card-body pt-0">
                <div className="row">
                  <div className="col-12 col-xl-8">
                    <table className="table gs-0 gy-1 m-0 mb-3">
                      <tbody>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.period}:</td>
                          <td className="fw-bold fs-6">{vatReturn.period.name}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.regime}:</td>
                          <td className="fw-bold fs-6">{taxMethodCompanyValue(lang, vatReturn.regime)}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.method}:</td>
                          <td className="fw-bold fs-6">{settingsForCompany.tax_declaration ? taxDeclarationCompanyValue(lang, settingsForCompany.tax_declaration) : lang.labels.none}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.status}:</td>
                          <td className="fw-bold fs-6">
                            <span className={`badge ${vatReturn.period.status === 'open' ? 'badge-light-success' : vatReturn.period.status === 'filed' ? 'badge-light-primary' : 'badge-light-danger'}`}>
                              {vatReturn.period.status === 'open' ? lang.labels.open : vatReturn.period.status === 'filed' ? lang.labels.filed : lang.labels.closed}
                            </span>
                            { vatReturn.period.status === 'open' &&
                              <button className="btn btn-sm btn-light-danger ms-3 py-1 px-3" onClick={handleClosePeriod} disabled={transitioning}>
                                {transitioning ? <span className="spinner-border spinner-border-sm align-middle"></span> : lang.labels.closePeriod}
                              </button>
                            }
                            { vatReturn.period.status === 'closed' &&
                              <>
                                <button className="btn btn-sm btn-danger ms-3 py-1 px-3" onClick={handleFilePeriod} disabled={transitioning}>
                                  {transitioning ? <span className="spinner-border spinner-border-sm align-middle"></span> : lang.labels.filePeriod}
                                </button>
                                <button className="btn btn-sm btn-light-success ms-2 py-1 px-3" onClick={handleReopenPeriod} disabled={transitioning}>
                                  {transitioning ? <span className="spinner-border spinner-border-sm align-middle"></span> : lang.labels.reopenPeriod}
                                </button>
                              </>
                            }
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            }
          </div>

          {/* AFC form */}
          { loading
            ?
            <div className="d-flex justify-content-center py-10">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{lang.labels.loading}</span>
              </div>
            </div>
            :
            vatReturn
            ?
            (() => {
              const map = casesMap()

              // Determine which rate pairs are present in the payload
              const presentRatePairs = RATE_PAIRS.filter(p => map.has(p.baseCode) || map.has(p.taxCode))
              const hasAcquisitions = map.has(ACQUISITIONS_PAIR.baseCode) || map.has(ACQUISITIONS_PAIR.taxCode)

              // Determine which input-tax codes are present
              const presentInputTaxCodes = INPUT_TAX_CODES.filter(c => map.has(c))
              const hasInputTaxSection = presentInputTaxCodes.length > 0 || map.has('479')

              return (
                <>
                  {/* Section I — Chiffre d'affaires */}
                  <div className="card card-flush mb-5">
                    <div className="card-header">
                      <div className="card-title">
                        <h5 className="m-0">{lang.labels.vatSectionTurnover}</h5>
                      </div>
                    </div>
                    <div className="card-body pt-0">
                      <div className="table-responsive">
                        <table className="table table-sm align-middle table-row-dashed mb-0">
                          <thead className="border-bottom">
                            <tr className="text-muted text-uppercase fw-bolder fs-8">
                              <th style={{ width: 55 }}>{lang.labels.caseNumber}</th>
                              <th>{lang.labels.description}</th>
                              <th className="text-end" style={{ minWidth: 120 }}>CHF</th>
                              <th className="text-end" style={{ minWidth: 120 }}>CHF</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Contre-prestations */}
                            <tr><td colSpan={4} className="text-muted text-uppercase fs-8 fw-bolder pt-4 pb-1 border-0">{lang.labels.vatCounterServices}</td></tr>
                            {SECTION_I_COUNTER.map(code => renderSectionIRow(code, map))}

                            {/* Deductions */}
                            <tr><td colSpan={4} className="text-muted text-uppercase fs-8 fw-bolder pt-4 pb-1 border-0">{lang.labels.vatDeductions}</td></tr>
                            {SECTION_I_DEDUCTIONS.map(code => renderSectionIRow(code, map))}
                            {renderSectionIRow('289', map)}

                            {/* Taxable turnover */}
                            <tr><td colSpan={4} className="pt-3 pb-0 border-0"></td></tr>
                            {renderSectionIRow('299', map)}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Section II — Calcul de l'impot */}
                  <div className="card card-flush mb-5">
                    <div className="card-header">
                      <div className="card-title">
                        <h5 className="m-0">{lang.labels.vatSectionTaxCalculation}</h5>
                      </div>
                    </div>
                    <div className="card-body pt-0">
                      {/* Sub-group: Impot du (rate lines) */}
                      <h6 className="text-muted text-uppercase fs-8 fw-bolder mb-2 mt-1">{lang.labels.vatTaxDue}</h6>
                      <div className="table-responsive mb-4">
                        <table className="table table-sm align-middle table-row-dashed mb-0">
                          <thead className="border-bottom">
                            <tr className="text-muted text-uppercase fw-bolder fs-8">
                              <th style={{ width: 55 }}>{lang.labels.caseNumber}</th>
                              <th>{lang.labels.description}</th>
                              <th className="text-end" style={{ minWidth: 120 }}>{lang.labels.base} (CHF)</th>
                              <th style={{ width: 55 }}>{lang.labels.caseNumber}</th>
                              <th className="text-end" style={{ minWidth: 70 }}>{lang.labels.taxRate}</th>
                              <th className="text-end" style={{ minWidth: 120 }}>{lang.labels.vatTax} (CHF)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {presentRatePairs.map(pair => renderRateLine(pair, map))}
                            {/* 379 total taxable turnover (if present) */}
                            {renderAggregateTaxRow('379', map, 'base')}
                            {/* Acquisitions (if present) */}
                            {hasAcquisitions && renderRateLine(ACQUISITIONS_PAIR, map)}
                            {/* 399 total tax due */}
                            {renderAggregateTaxRow('399', map, 'tax')}
                          </tbody>
                        </table>
                      </div>

                      {/* Sub-group: Impot prealable / Credit (only if codes present) */}
                      { hasInputTaxSection &&
                        <>
                          <h6 className="text-muted text-uppercase fs-8 fw-bolder mb-2">{lang.labels.vatInputTax}</h6>
                          <div className="table-responsive mb-4">
                            <table className="table table-sm align-middle table-row-dashed mb-0">
                              <thead className="border-bottom">
                                <tr className="text-muted text-uppercase fw-bolder fs-8">
                                  <th style={{ width: 55 }}>{lang.labels.caseNumber}</th>
                                  <th>{lang.labels.description}</th>
                                  <th className="text-end" style={{ minWidth: 120 }}>CHF</th>
                                  <th className="text-end" style={{ minWidth: 120 }}>CHF</th>
                                </tr>
                              </thead>
                              <tbody>
                                {presentInputTaxCodes.map(code => renderInputTaxRow(code, map))}
                                {renderInputTaxRow('479', map)}
                              </tbody>
                            </table>
                          </div>
                        </>
                      }

                      {/* Sub-group: Solde */}
                      <h6 className="text-muted text-uppercase fs-8 fw-bolder mb-2">{lang.labels.vatBalance}</h6>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead className="border-bottom">
                            <tr className="text-muted text-uppercase fw-bolder fs-8">
                              <th style={{ width: 55 }}>{lang.labels.caseNumber}</th>
                              <th>{lang.labels.description}</th>
                              <th style={{ minWidth: 120 }}></th>
                              <th className="text-end" style={{ minWidth: 120 }}>CHF</th>
                            </tr>
                          </thead>
                          <tbody>
                            {BALANCE_CODES.map(code => renderInputTaxRow(code, map))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Section III — Autres mouvements */}
                  <div className="card card-flush mb-5">
                    <div className="card-header">
                      <div className="card-title">
                        <h5 className="m-0">{lang.labels.vatSectionOtherFlows}</h5>
                      </div>
                    </div>
                    <div className="card-body pt-0">
                      <div className="table-responsive">
                        <table className="table table-sm align-middle table-row-dashed mb-0">
                          <thead className="border-bottom">
                            <tr className="text-muted text-uppercase fw-bolder fs-8">
                              <th style={{ width: 55 }}>{lang.labels.caseNumber}</th>
                              <th style={{ width: 30 }}></th>
                              <th>{lang.labels.description}</th>
                              <th className="text-end" style={{ minWidth: 120 }}>CHF</th>
                            </tr>
                          </thead>
                          <tbody>
                            {SECTION_III_CODES.map(code => renderRow(code, map))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )
            })()
            :
            periods.length === 0
            ?
            <div className="card card-flush">
              <div className="card-body text-center py-10">
                <span className="text-muted fw-bold">{lang.labels.noRecordsFound}</span>
              </div>
            </div>
            :
            null
          }
        </>
        :
        <AppPreloader></AppPreloader>
      }
      { companyForUser && selectedPeriod &&
        <AppModalVatAdjustmentEdit
          idCompany={companyForUser.id}
          idPeriod={selectedPeriod.id}
          caseCode={editingCase.code}
          label={editingCase.label}
          currentAmount={editingCase.amount}
          currentNote={editingCase.note}
          currentVouchers={editingCase.vouchers}
          isFiled={isFiled ?? false}
          onSaved={handleAdjustmentSaved}
        />
      }
    </div>
  )
};

export default VatReturnListPage;
